import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Income, INCOME_CATEGORIES } from "@/lib/api";
import { fmtDate, fmtMoney } from "@/lib/bill-utils";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Plus, Trash2, Loader2, CheckCircle2, Circle, FileSpreadsheet } from "lucide-react";
import { toast } from "sonner";

const emptyEntry = {
  client_name: "",
  category: "Install deposit 50%",
  amount: "",
  expected_date: "",
  received: false,
  note: "",
};

const categoryChipClass = (cat) => {
  if (cat.startsWith("Install deposit"))
    return "bg-sky-50 text-sky-800 border-sky-200";
  if (cat === "Service call")
    return "bg-amber-50 text-amber-800 border-amber-300";
  if (cat === "Factory repair")
    return "bg-purple-50 text-purple-800 border-purple-200";
  return "bg-neutral-50 text-neutral-700 border-neutral-300";
};

const IncomePage = () => {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all"); // all | pending | received
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyEntry);
  const [busy, setBusy] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();

  const load = async () => {
    try {
      setLoading(true);
      const params =
        filter === "pending"
          ? { received: false }
          : filter === "received"
            ? { received: true }
            : {};
      const data = await Income.list(params);
      setRows(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  // Open the Add dialog when /income?new=1 is hit (from FAB / header button)
  useEffect(() => {
    if (searchParams.get("new") === "1") {
      startNew();
      searchParams.delete("new");
      setSearchParams(searchParams, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const startNew = () => {
    setEditingId(null);
    setForm({
      ...emptyEntry,
      expected_date: new Date().toISOString().slice(0, 10),
    });
    setOpen(true);
  };

  const startEdit = (row) => {
    setEditingId(row.id);
    setForm({
      client_name: row.client_name || "",
      category: row.category || "Other",
      amount: String(row.amount ?? ""),
      expected_date: row.expected_date || "",
      received: !!row.received,
      note: row.note || "",
    });
    setOpen(true);
  };

  const save = async () => {
    if (!form.client_name.trim()) return toast.error("Client name is required");
    if (!form.category) return toast.error("Category is required");
    const amt = Number(form.amount);
    if (!amt || amt <= 0) return toast.error("Amount must be > 0");
    if (!form.expected_date) return toast.error("Expected date is required");

    setBusy(true);
    try {
      const payload = {
        client_name: form.client_name.trim(),
        category: form.category,
        amount: amt,
        expected_date: form.expected_date,
        received: form.received,
        note: form.note || "",
      };
      if (editingId) {
        await Income.update(editingId, payload);
        toast.success("Income updated");
      } else {
        await Income.create(payload);
        toast.success("Income added");
      }
      setOpen(false);
      setForm(emptyEntry);
      setEditingId(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save");
    } finally {
      setBusy(false);
    }
  };

  const toggleReceived = async (row) => {
    try {
      await Income.update(row.id, { received: !row.received });
      load();
    } catch {
      toast.error("Could not update");
    }
  };

  const remove = async (row) => {
    if (!window.confirm(`Delete income from ${row.client_name}?`)) return;
    try {
      await Income.remove(row.id);
      toast.success("Deleted");
      load();
    } catch {
      toast.error("Could not delete");
    }
  };

  const total = rows.reduce((s, r) => s + (Number(r.amount) || 0), 0);
  const received = rows
    .filter((r) => r.received)
    .reduce((s, r) => s + (Number(r.amount) || 0), 0);
  const pending = rows
    .filter((r) => !r.received)
    .reduce((s, r) => s + (Number(r.amount) || 0), 0);

  return (
    <div data-testid="income-page" className="fade-rise">
      <div className="swiss-label mb-3">Income</div>
      <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tight leading-tight">
        Money coming in
      </h1>
      <p className="text-neutral-600 mt-3 max-w-2xl">
        Track deposits, service calls, repairs and other receivables. Tick each
        entry off when the money lands.
      </p>

      <section className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-4 md:gap-6">
        <div className="swiss-card">
          <div className="swiss-label">Total Booked</div>
          <div className="mt-3 font-display font-black text-2xl sm:text-3xl tabular-nums whitespace-nowrap">
            {fmtMoney(total)}
          </div>
        </div>
        <div className="swiss-card">
          <div className="swiss-label">Received</div>
          <div
            className="mt-3 font-display font-black text-2xl sm:text-3xl tabular-nums whitespace-nowrap"
            style={{ color: "#008A00" }}
          >
            {fmtMoney(received)}
          </div>
        </div>
        <div className="swiss-card">
          <div className="swiss-label">Pending</div>
          <div
            className="mt-3 font-display font-black text-2xl sm:text-3xl tabular-nums whitespace-nowrap"
            style={{ color: "#B45309" }}
          >
            {fmtMoney(pending)}
          </div>
        </div>
      </section>

      <section className="mt-8 flex flex-wrap gap-2 items-center justify-between">
        <div className="flex gap-2">
          {[
            { k: "all", l: "All" },
            { k: "pending", l: "Pending" },
            { k: "received", l: "Received" },
          ].map((f) => (
            <button
              key={f.k}
              data-testid={`filter-${f.k}`}
              onClick={() => setFilter(f.k)}
              className={`px-3 py-2 border-2 text-[11px] uppercase tracking-[0.18em] font-bold transition-colors ${
                filter === f.k
                  ? "bg-black text-white border-black"
                  : "bg-white text-neutral-700 border-neutral-300 hover:border-black"
              }`}
            >
              {f.l}
            </button>
          ))}
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            data-testid="upload-quote-btn"
            onClick={() => navigate("/income/from-quote")}
            className="border-2 border-black bg-white text-black font-bold uppercase tracking-widest text-xs px-4 py-3 hover:bg-black hover:text-white transition-colors inline-flex items-center gap-2"
          >
            <FileSpreadsheet className="h-4 w-4" /> Upload Quote → 3 Incomes
          </button>
          <button
            data-testid="add-income-btn"
            onClick={startNew}
            className="border-2 border-black bg-black text-white font-bold uppercase tracking-widest text-xs px-5 py-3 hover:bg-white hover:text-black transition-colors inline-flex items-center gap-2"
          >
            <Plus className="h-4 w-4" /> Add Income
          </button>
        </div>
      </section>

      <section className="mt-6">
        {loading && (
          <div className="text-sm text-neutral-500">Loading…</div>
        )}
        {!loading && rows.length === 0 && (
          <div
            data-testid="income-empty"
            className="swiss-card text-center py-16 border-dashed"
          >
            <div className="swiss-label">No income entries yet</div>
            <div className="font-display font-extrabold text-2xl mt-2">
              Add your first
            </div>
            <p className="text-neutral-500 mt-2 max-w-md mx-auto">
              Log every expected deposit, service call and repair so the weekly
              tab knows what's due in.
            </p>
          </div>
        )}
        {!loading && rows.length > 0 && (
          <div className="bg-white border border-neutral-200">
            {/* header row (desktop) */}
            <div className="hidden md:grid grid-cols-12 gap-3 px-4 py-3 border-b border-neutral-200 swiss-label">
              <div className="col-span-1">Paid</div>
              <div className="col-span-3">Client</div>
              <div className="col-span-3">Category</div>
              <div className="col-span-2">Expected</div>
              <div className="col-span-2 text-right">Amount</div>
              <div className="col-span-1" />
            </div>
            <ul data-testid="income-list" className="stagger">
              {rows.map((r) => (
                <li
                  key={r.id}
                  data-testid={`income-row-${r.id}`}
                  className="grid grid-cols-12 gap-3 px-4 py-4 border-b border-neutral-100 last:border-0 items-center hover:bg-neutral-50"
                >
                  <div className="col-span-1 flex items-center">
                    <button
                      data-testid={`income-toggle-${r.id}`}
                      onClick={() => toggleReceived(r)}
                      aria-label={
                        r.received ? "Mark as pending" : "Mark as received"
                      }
                      className={`h-8 w-8 border-2 flex items-center justify-center transition-colors ${
                        r.received
                          ? "bg-green-600 border-green-600 text-white"
                          : "bg-white border-neutral-300 text-neutral-400 hover:border-black hover:text-black"
                      }`}
                    >
                      {r.received ? (
                        <CheckCircle2 className="h-4 w-4" strokeWidth={2.5} />
                      ) : (
                        <Circle className="h-4 w-4" strokeWidth={2} />
                      )}
                    </button>
                  </div>
                  <button
                    onClick={() => startEdit(r)}
                    className="col-span-11 md:col-span-3 text-left min-w-0"
                  >
                    <div className="font-display font-bold truncate">
                      {r.client_name}
                    </div>
                    {r.note && (
                      <div className="text-xs text-neutral-500 truncate">
                        {r.note}
                      </div>
                    )}
                  </button>
                  <div className="col-span-6 md:col-span-3">
                    <span className={`chip ${categoryChipClass(r.category)}`}>
                      {r.category}
                    </span>
                  </div>
                  <div className="col-span-3 md:col-span-2 text-sm whitespace-nowrap">
                    {fmtDate(r.expected_date)}
                  </div>
                  <div className="col-span-3 md:col-span-2 text-right font-display font-extrabold mono tabular-nums whitespace-nowrap">
                    {fmtMoney(r.amount)}
                  </div>
                  <div className="col-span-12 md:col-span-1 flex justify-end">
                    <button
                      data-testid={`income-delete-${r.id}`}
                      onClick={() => remove(r)}
                      className="text-neutral-400 hover:text-red-600 transition-colors"
                      aria-label="Delete"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        )}
      </section>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent
          data-testid="income-dialog"
          className="rounded-none border-2 border-black"
        >
          <DialogHeader>
            <DialogTitle className="font-display font-extrabold text-2xl">
              {editingId ? "Edit Income" : "Add Income"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <label className="block">
              <div className="swiss-label mb-1">Client Name</div>
              <Input
                data-testid="input-client-name"
                className="swiss-input"
                value={form.client_name}
                onChange={(e) =>
                  setForm({ ...form, client_name: e.target.value })
                }
                placeholder="Jane Smith"
              />
            </label>
            <label className="block">
              <div className="swiss-label mb-1">Category</div>
              <select
                data-testid="input-category"
                className="swiss-input w-full cursor-pointer"
                value={form.category}
                onChange={(e) =>
                  setForm({ ...form, category: e.target.value })
                }
              >
                {INCOME_CATEGORIES.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
            </label>
            <div className="grid grid-cols-2 gap-4">
              <label className="block">
                <div className="swiss-label mb-1">Amount (AUD)</div>
                <Input
                  data-testid="input-amount"
                  className="swiss-input mono"
                  value={form.amount}
                  onChange={(e) =>
                    setForm({ ...form, amount: e.target.value })
                  }
                  inputMode="decimal"
                  placeholder="0.00"
                />
              </label>
              <label className="block">
                <div className="swiss-label mb-1">Expected Date</div>
                <Input
                  data-testid="input-expected-date"
                  type="date"
                  className="swiss-input"
                  value={form.expected_date}
                  onChange={(e) =>
                    setForm({ ...form, expected_date: e.target.value })
                  }
                />
              </label>
            </div>
            <label className="flex items-center gap-3 cursor-pointer select-none">
              <input
                data-testid="input-received"
                type="checkbox"
                checked={form.received}
                onChange={(e) =>
                  setForm({ ...form, received: e.target.checked })
                }
                className="h-5 w-5 accent-black"
              />
              <span className="text-sm font-bold uppercase tracking-widest">
                Money received
              </span>
            </label>
            <label className="block">
              <div className="swiss-label mb-1">Note (optional)</div>
              <Textarea
                data-testid="input-note"
                className="swiss-input resize-none"
                rows={2}
                value={form.note}
                onChange={(e) => setForm({ ...form, note: e.target.value })}
                placeholder="Reference, job name, etc."
              />
            </label>
          </div>
          <DialogFooter className="gap-2">
            <button
              data-testid="income-cancel-btn"
              onClick={() => setOpen(false)}
              className="border-2 border-neutral-300 bg-white text-neutral-700 font-bold uppercase tracking-widest text-xs px-4 py-3 hover:border-black hover:text-black"
            >
              Cancel
            </button>
            <button
              data-testid="income-save-btn"
              onClick={save}
              disabled={busy}
              className="border-2 border-black bg-black text-white font-bold uppercase tracking-widest text-xs px-4 py-3 hover:bg-white hover:text-black transition-colors disabled:opacity-50 inline-flex items-center gap-2"
            >
              {busy && <Loader2 className="h-4 w-4 animate-spin" />}
              {editingId ? "Save Changes" : "Add Income"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default IncomePage;
